/**
 * Recently viewed — browser-local, product handles only.
 *
 * Two jobs: record the handle when a product page is open, and render the rail wherever a
 * [data-drv-rv] section exists. The rail stays hidden unless at least one card actually renders,
 * so a first-time visitor never meets an empty heading.
 *
 * What is stored: an array of product handles. No titles, no prices, no ids, nothing identifying.
 * Titles, images and prices are fetched live from Shopify at render time, which is the only way a
 * stale price cannot be shown — the reason handles are stored rather than whole cards.
 *
 * A handle that 404s (product unpublished or renamed since the visit) is dropped from the list
 * instead of rendering a card to nowhere.
 */
(function () {
  'use strict';

  var KEY = 'drv:recently-viewed';
  var MAX_STORED = 12;

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.filter(function (h) { return typeof h === 'string' && /^[a-z0-9-]+$/i.test(h); });
    } catch (e) {
      // Private mode, disabled storage, or corrupt JSON. The feature is optional; never throw.
      return [];
    }
  }

  function write(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_STORED))); } catch (e) { /* not fatal */ }
  }

  // Works for /products/x and /collections/y/products/x alike, so no PDP template edit is needed.
  function currentHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function record() {
    var h = currentHandle();
    if (!h) return;
    var list = read().filter(function (x) { return x !== h; });
    list.unshift(h);
    write(list);
  }

  function moneyFormat() {
    var el = document.querySelector('[data-drv-money-format]');
    return (el && el.getAttribute('data-drv-money-format')) || (window.theme && theme.moneyFormat) || '⁦{{amount}} EGP⁩';
  }

  function money(cents, fmt) {
    /* Latin digits, no thousands separator, no ".00" (a real fraction keeps two places) - same display as snippets/drv-money.liquid */
    var v = (cents / 100).toLocaleString('en-US', { useGrouping: false, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 });
    return (fmt || '⁦{{amount}} EGP⁩').replace(/\{\{\s*amount\s*\}\}/, v);
  }

  function card(p) {
    var li = document.createElement('li');
    li.className = 'drv-bs-item';

    var a = document.createElement('a');
    a.className = 'drv-bs-card';
    a.href = p.url;

    var media = document.createElement('span');
    media.className = 'drv-bs-media';
    if (p.featured_image) {
      var img = document.createElement('img');
      img.className = 'drv-bs-img';
      img.loading = 'lazy';
      img.decoding = 'async';
      // width/height reserve the box before the image lands, matching the rail's CLS behaviour.
      img.width = 300;
      img.height = 300;
      img.alt = p.title || '';
      /* 2026-09-16: the legacy "_300x300" suffix delivered one fixed 300px file into a 154 CSS px card — soft on every
         retina phone. Modern width parameter + srcset, so the browser picks by its own pixel density. */
      var u = p.featured_image.split('?')[0];
      var w = function (n) { return u + '?width=' + n; };
      img.src = w(400);
      img.srcset = [300, 400, 520, 640, 800].map(function (n) { return w(n) + ' ' + n + 'w'; }).join(', ');
      img.sizes = '(min-width: 990px) 260px, (min-width: 750px) 30vw, 46vw';
      media.appendChild(img);
    }
    a.appendChild(media);

    var name = document.createElement('span');
    name.className = 'drv-bs-name';
    name.textContent = p.title || '';
    a.appendChild(name);

    var price = document.createElement('span');
    price.className = 'drv-bs-price';
    var now = document.createElement('bdi');
    now.textContent = money(p.price, moneyFormat());
    price.appendChild(now);
    a.appendChild(price);

    li.appendChild(a);
    return li;
  }

  function render() {
    var sections = document.querySelectorAll('[data-drv-rv]');
    if (!sections.length) return;

    var here = currentHandle();
    var wanted = read().filter(function (h) { return h !== here; });
    if (!wanted.length) return;

    var limit = parseInt(sections[0].getAttribute('data-limit'), 10) || 6;
    wanted = wanted.slice(0, limit);

    /* 2026-09-24: was /products/<handle>.js, whose `title` is the DEFAULT-LOCALE (English) one -
       the Arabic customer-facing name is the drv.display_name_ar metafield, which no JSON endpoint
       returns. So the rail printed English names on the Arabic storefront (six of six, measured).
       ?view=drv-me is the theme's own tiny Liquid JSON view and renders the governed display name
       through snippets/drv-title.liquid, plus the image, url and price this rail needs. Handles are
       still the only thing stored, and price is still fetched live, so neither the privacy choice
       above nor the stale-price guarantee changes. */
    Promise.all(wanted.map(function (h) {
      /* NO Accept: application/json here. With that header Shopify ignores ?view= and answers with
         its own {"product":{...}} payload, whose title is the English one again - measured on
         preview, which is how this was caught. sections/drv-account-hub.liquid calls the same view
         the same way, without the header. */
      return fetch('/products/' + encodeURIComponent(h) + '?view=drv-me', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var live = [];
      var good = [];
      products.forEach(function (p, i) {
        if (p && p.title && p.url) { live.push(p); good.push(wanted[i]); }
      });

      // Forget handles that no longer resolve rather than keep retrying them on every page view.
      if (good.length !== wanted.length) {
        var keep = read().filter(function (h) { return wanted.indexOf(h) === -1 || good.indexOf(h) !== -1; });
        write(keep);
      }
      if (!live.length) return;

      Array.prototype.forEach.call(sections, function (sec) {
        var list = sec.querySelector('[data-drv-rv-list]');
        if (!list) return;
        list.textContent = '';
        live.forEach(function (p) { list.appendChild(card(p)); });
        sec.removeAttribute('hidden');
      });
    });
  }

  record();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render, { once: true });
  } else {
    render();
  }
})();
